import { TramFront, Radar, Compass, Database, ExternalLink } from 'lucide-react';

/**
 * Homepage spotlight for ZET uživo — the live Zagreb transit map.
 *
 * It sits directly after the SeaCloud Mute spotlight and runs the same
 * `.spotlight-*` classes with the layout mirrored and a lighter ground, so the
 * two read as a pair of things we built rather than one section repeated.
 *
 * Unlike Mute this is a hosted site, not a download, so the call to action
 * leaves for the app itself rather than an internal product page.
 */
const bullets = [
  { icon: TramFront, text: 'Around 300 trams and buses, refreshed every few seconds' },
  { icon: Radar, text: 'Vehicles that stop reporting GPS are estimated from the timetable' },
  { icon: Compass, text: 'Direction of travel derived from consecutive fixes — the feed carries none' },
  { icon: Database, text: "Built on the City of Zagreb's open GTFS-Realtime data" },
];

export default function ZetSpotlight() {
  return (
    <section className="spotlight-section spotlight-section-alt">
      <div className="section-inner spotlight-inner spotlight-inner-reverse">
        <div className="spotlight-text">
          <span className="section-eyebrow spotlight-eyebrow">Open data project from SeaCloud Software</span>
          <h2 className="spotlight-title">ZET uživo</h2>
          <p className="spotlight-desc">
            A live map of every tram and bus in Zagreb, built on the city&rsquo;s open
            real-time feed — including the vehicles that have gone quiet, whose
            positions are estimated from the timetable rather than left off the map.
          </p>

          <ul className="spotlight-bullets">
            {bullets.map((b) => {
              const Icon = b.icon;
              return (
                <li key={b.text}>
                  <span className="spotlight-bullet-icon">
                    <Icon size={15} strokeWidth={2} />
                  </span>
                  {b.text}
                </li>
              );
            })}
          </ul>

          <div className="spotlight-actions">
            {/* No nofollow: this link is meant to pass authority to the map,
                which is most of why the section exists. noreferrer is the site
                convention and costs nothing here — neither property runs
                analytics, so there is no referrer worth preserving. */}
            <a
              className="spotlight-open"
              href="https://prijevoz.seacloud-software.hr/"
              target="_blank"
              rel="noopener noreferrer"
            >
              Open the live map
              <ExternalLink size={15} strokeWidth={2} />
            </a>
            <span className="product-footer-note">Free to use · in Croatian</span>
          </div>
        </div>

        <div className="spotlight-visual">
          <div className="site-mockup">
            <div className="site-mockup-bar">
              <span className="site-mockup-dot" />
              <span className="site-mockup-dot" />
              <span className="site-mockup-dot" />
              <span className="site-mockup-url">prijevoz.seacloud-software.hr</span>
            </div>
            <img
              className="site-mockup-shot"
              src="/images/zet-uzivo/map.jpg"
              alt="The ZET uživo map showing live tram and bus positions across Zagreb."
              width={1040}
              height={668}
              loading="lazy"
            />
          </div>
        </div>
      </div>
    </section>
  );
}
